import React, { useState, useRef, useEffect } from 'react';
import JuridictionFilter from './JuridictionFilter/JuridictionFilter';
import DateRangeFilter from './DateRangeFilter/DateRangeFilter';
import CheckboxFilter from './CheckboxFilter/CheckboxFilter';
import TextInputFilter from './TextInputFilter/TextInputFilter';
import ActiveFilters from './ActiveFilters/ActiveFilters';
import FilterItem from './FilterItem/FilterItem';
import './filtre.css';

const AdvancedFilters = ({ onFilterChange }) => {
    // États pour les filtres
    const [openDropdown, setOpenDropdown] = useState(null);
    const [selectedFilters, setSelectedFilters] = useState({
        juridiction: [],
        sections: [],
        dateRange: { start: null, end: null },
        portee: [],
        publication: [],
        dispositif: [],
        textesCites: [],
        textesNonCites: []
    });

    const [textInput, setTextInput] = useState({
        textesCites: "",
        textesNonCites: ""
    });

    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setOpenDropdown(null);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        onFilterChange(selectedFilters);
    }, [selectedFilters, onFilterChange]);

    const toggleDropdown = (dropdown) => {
        setOpenDropdown(openDropdown === dropdown ? null : dropdown);
    };

    const handleFilterSelect = (category, value) => {
        setSelectedFilters(prev => {
            const updated = { ...prev };

            if (Array.isArray(updated[category])) {
                if (updated[category].includes(value)) {
                    updated[category] = updated[category].filter(item => item !== value);
                } else {
                    updated[category] = [...updated[category], value];
                }
            } else {
                updated[category] = value;
            }

            return updated;
        });
    };

    const handleDateRangeChange = (type, value) => {
        setSelectedFilters(prev => ({
            ...prev,
            dateRange: {
                ...prev.dateRange,
                [type]: value
            }
        }));
    };

    const handleTextInputChange = (category, value) => {
        setTextInput(prev => ({
            ...prev,
            [category]: value
        }));
    };

    const handleTextInputSubmit = (category, event) => {
        if (event.key === 'Enter' && textInput[category].trim()) {
            setSelectedFilters(prev => ({
                ...prev,
                [category]: [...prev[category], textInput[category].trim()]
            }));
            setTextInput(prev => ({
                ...prev,
                [category]: ""
            }));
        }
    };

    const removeTextTag = (category, value) => {
        setSelectedFilters(prev => ({
            ...prev,
            [category]: prev[category].filter(item => item !== value)
        }));
    };

    const clearFilters = (category) => {
        if (category === 'dateRange') {
            setSelectedFilters(prev => ({
                ...prev,
                dateRange: { start: null, end: null }
            }));
        } else {
            setSelectedFilters(prev => ({
                ...prev,
                [category]: Array.isArray(prev[category]) ? [] : null
            }));
        }
    };

    const clearAllFilters = () => {
        setSelectedFilters({
            juridiction: [],
            sections: [],
            dateRange: { start: null, end: null },
            portee: [],
            publication: [],
            dispositif: [],
            textesCites: [],
            textesNonCites: []
        });
    };

    // Organiser les juridictions comme sur Pappers
    const juridictionCategories = {
        "Cours suprêmes et d'appel": [
            "CONSEIL CONSTITUTIONNEL",
            "CONSEIL D'ETAT",
            "COUR DE CASSATION",
            "COUR COMMUNE DE JUSTICE ET D'ARBITRAGE (CCJA)",
            "COUR DES COMPTES",
            "COUR D'APPEL D'ABIDJAN",
            "COUR D'APPEL DE BOUAKE",
            "COUR D'APPEL DE COMMERCE D'ABIDJAN",
            "COUR D'APPEL DE DALOA",
            "COUR D'APPEL DE KORHOGO"
        ],
        "Tribunaux de première instance": [
            "TRIBUNAL DE COMMERCE D'ABIDJAN",
            "TRIBUNAL DE PREMIERE INSTANCE D'ABIDJAN-PLATEAU",
            "TRIBUNAL DE PREMIERE INSTANCE DE BOUAFLE",
            "TRIBUNAL DE PREMIERE INSTANCE DE BOUAKE",
            "TRIBUNAL DE PREMIERE INSTANCE DE DALOA",
            "TRIBUNAL DE PREMIERE INSTANCE DE DIVO",
            "TRIBUNAL DE PREMIERE INSTANCE DE GAGNOA",
            "TRIBUNAL DE PREMIERE INSTANCE DE MAN",
            "TRIBUNAL DE PREMIERE INSTANCE DE SAN-PEDRO",
            "TRIBUNAL DE PREMIERE INSTANCE DE YOPOUGON",
            "TRIBUNAL CRIMINEL DE MAN"
        ],
        "Tribunaux spécialisés": [
            "TRIBUNAL DE TRAVAIL D'ABIDJAN",
            "TRIBUNAL DE TRAVAIL DE BOUAFLE",
            "TRIBUNAL DE TRAVAIL DE MAN",
            "TRIBUNAL DU TRAVAIL DE GAGNOA"
        ],
        "Sections de tribunal": [
            "SECTION DE TRIBUNAL D'ADZOPE",
            "SECTION DE TRIBUNAL D'ISSIA",
            "SECTION DE TRIBUNAL D'OUME",
            "SECTION DE TRIBUNAL DE BONGOUANOU",
            "SECTION DE TRIBUNAL DE DABOU",
            "SECTION DE TRIBUNAL DE DANANE",
            "SECTION DE TRIBUNAL DE DIVO",
            "SECTION DE TRIBUNAL DE GAGNOA",
            "SECTION DE TRIBUNAL DE GUIGLO",
            "SECTION DE TRIBUNAL DE LAKOTA",
            "SECTION DE TRIBUNAL DE SASSANDRA",
            "SECTION DE TRIBUNAL DE SEGUELA",
            "SECTION DE TRIBUNAL DE SINFRA",
            "SECTION DE TRIBUNAL DE SOUBRE",
            "SECTION DE TRIBUNAL DE TABOU",
            "SECTION DE TRIBUNAL DE TOUBA",
            "SECTION DE TRIBUNAL SEGUELA"
        ]
    };

    const filtreOptions = {
        portee: ["Nationale", "Internationale", "Locale", "Régionale"],
        publication: ["Publiée", "Non publiée"],
        dispositif: ["Cassation", "Rejet", "Annulation", "Confirmation", "Infirmation", "Irrecevabilité"]
    };

    return (
        <div className="advanced-filters-container" ref={dropdownRef}>
            <div className="filters-grid">
                {/* Filtre Juridiction */}
                <JuridictionFilter
                    isActive={openDropdown === 'juridiction'}
                    selectedItems={selectedFilters.juridiction}
                    onToggle={toggleDropdown}
                    onSelect={handleFilterSelect}
                    onClear={clearFilters}
                    juridictionCategories={juridictionCategories}
                />

                {/* Filtre Sections (avec cadenas) */}
                {/* <FilterItem
                    label="Sections"
                    isLocked={true}
                    lockIcon={
                        <svg className="lock-icon" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                            <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                        </svg>
                    }
                    onToggle={() => { }}
                /> */}

                {/* Filtre Date (amélioré pour plage de dates) */}
                <DateRangeFilter
                    isActive={openDropdown === 'date'}
                    dateRange={selectedFilters.dateRange}
                    onToggle={toggleDropdown}
                    onDateChange={handleDateRangeChange}
                    onClear={clearFilters}
                />

                <TextInputFilter
                    filterName="textesCites"
                    displayName="Textes de loi cités"
                    isActive={openDropdown === 'textesCites'}
                    selectedItems={selectedFilters.textesCites}
                    inputValue={textInput.textesCites}
                    onToggle={toggleDropdown}
                    onInputChange={handleTextInputChange}
                    onInputSubmit={handleTextInputSubmit}
                    onRemoveTag={removeTextTag}
                    onClear={clearFilters}
                />

                {/* Filtre Portée */}
                {/* <CheckboxFilter
                    filterName="portee"
                    displayName="Portée"
                    isActive={openDropdown === 'portee'}
                    selectedItems={selectedFilters.portee}
                    options={filtreOptions.portee}
                    onToggle={toggleDropdown}
                    onSelect={handleFilterSelect}
                    onClear={clearFilters}
                /> */}

                {/* <CheckboxFilter
                    filterName="publication"
                    displayName="Publication"
                    isActive={openDropdown === 'publication'}
                    selectedItems={selectedFilters.publication}
                    options={filtreOptions.publication}
                    onToggle={toggleDropdown}
                    onSelect={handleFilterSelect}
                    onClear={clearFilters}
                />

                <CheckboxFilter
                    filterName="dispositif"
                    displayName="Dispositif"
                    isActive={openDropdown === 'dispositif'}
                    selectedItems={selectedFilters.dispositif}
                    options={filtreOptions.dispositif}
                    onToggle={toggleDropdown}
                    onSelect={handleFilterSelect}
                    onClear={clearFilters}
                />

                <TextInputFilter
                    filterName="textesNonCites"
                    displayName="Textes de loi non cités"
                    isActive={openDropdown === 'textesNonCites'}
                    selectedItems={selectedFilters.textesNonCites}
                    inputValue={textInput.textesNonCites}
                    onToggle={toggleDropdown}
                    onInputChange={handleTextInputChange}
                    onInputSubmit={handleTextInputSubmit}
                    onRemoveTag={removeTextTag}
                    onClear={clearFilters}
                /> */}
            </div>

            <ActiveFilters
                selectedFilters={selectedFilters}
                onRemoveFilter={handleFilterSelect}
                onRemoveTextTag={removeTextTag}
                onClearDateRange={clearFilters}
                onClearAllFilters={clearAllFilters}
            />
        </div>
    );
};

export default AdvancedFilters;